import React, { useEffect, useState } from "react";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import DataChart, { ChartElement } from "../components/DataChart";
import { Button, Col, Divider, Row, Select, Space } from "antd";
import moment, { DurationInputArg2 } from "moment";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as Strings from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";
import * as String from "../commons/string";

import StatsCard from "../components/StatsCard";
import SearchDate from "../components/SearchDate";

type SearchUserStatsType = {
    startDate: string;
    endDate: string;
    period: "YEAR" | "MONTH" | "DAY";
};

type ChartDataType = {
    name: string;
    type: string;
    data: ChartElement[];
};

function Dashboard() {
    const [userCount, setUserCount] = useState<Types.UserCountDto>();
    const [visitorCount, setVisitorCount] = useState<Types.VisitorCountDto>();
    const [rentVisitorCount, setRentVisitorCount] = useState<Types.VisitorCountDto>();

    const [searchUserGroupBy, setSearchUserGroupBy] = useState<string>("DAY");
    const [searchUserStartDate, setSearchUserStartDate] = useState(moment().subtract(30, "d").format(Common.FORMAT_DATE));
    const [searchUserEndDate, setSearchUserEndDate] = useState(moment().format(Common.FORMAT_DATE));

    const [userStats, setUserStats] = useState<Types.UserStatsDto>();
    const [chartSeriesUser, setChartSeriesUser] = useState<Array<ChartDataType>>([]);

    const [searchVisitorGroupBy, setSearchVisitorGroupBy] = useState<string>("DAY");
    const [searchVisitorStartDate, setSearchVisitorStartDate] = useState(moment().subtract(30, "d").format(Common.FORMAT_DATE));
    const [searchVisitorEndDate, setSearchVisitorEndDate] = useState(moment().format(Common.FORMAT_DATE));

    const [visitorStats, setVisitorStats] = useState<Types.VisitorStatsDto>();
    const [chartSeriesVisitor, setChartSeriesVisitor] = useState<Array<ChartDataType>>([]);

    const [searchVisitorRentGroupBy, setSearchVisitorRentGroupBy] = useState<string>("DAY");
    const [searchVisitorRentStartDate, setSearchVisitorRentStartDate] = useState(moment().subtract(30, "d").format(Common.FORMAT_DATE));
    const [searchVisitorRentEndDate, setSearchVisitorRentEndDate] = useState(moment().format(Common.FORMAT_DATE));

    const [visitorRentStats, setVisitorRentStats] = useState<Types.RentVisitorStatsDto>();
    const [chartSeriesVisitorRent, setChartSeriesVisitorRent] = useState<Array<ChartDataType>>([]);

    const [commonChartOptions, setCommonChartOptions] = useState<Record<string, any>>({
        chart: {
            type: "line",
            toolbar: {
                show: true,
                tools: {
                    download: false,
                    selection: false,
                    pan: false,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                },
            },
        },
        xaxis: {
            type: "number",
            labels: {
                style: { cssClass: "fw-rg fs-xs fc-title1" },
                formatter: function (val: string, index: number) {
                    return val;
                },
            },
            tooltip: {
                enabled: false,
            },
        },
        yaxis: [
            {
                title: { text: "누적" },
                labels: {
                    style: { cssClass: "fw-rg fs-xs fc-title1" },
                    formatter: function (val: number, index: number) {
                        return val;
                    },
                },
            },
            {
                title: { text: "증가량" },
                opposite: true,
                labels: {
                    style: { cssClass: "fw-rg fs-xs fc-title1" },
                    formatter: function (val: number, index: number) {
                        return val;
                    },
                },
            },
        ],
        tooltip: {
            enable: true,
            marker: {
                show: true,
            },
            x: {
                show: true,
                formatter: function (value: number, { series, seriesIndex, dataPointIndex, w }: any) {
                    console.log(moment(w.globals.categoryLabels[dataPointIndex]));
                    const xDateTime = moment(w.globals.categoryLabels[dataPointIndex]);
                    if (xDateTime.isValid()) {
                        const weekDays = ["(일)", "(월)", "(화)", "(수)", "(목)", "(금)", "(토)"];
                        return w.globals.categoryLabels[dataPointIndex] + " " + weekDays[xDateTime.day()];
                    }
                    return w.globals.categoryLabels[dataPointIndex]; // The formatter function overrides format property
                },
            },
            y: {
                formatter: function (value: number, { series, seriesIndex, dataPointIndex, w }: any) {
                    return value;
                },
            },
        },
        theme: {
            palette: "palette2",
        },
        markers: {
            size: [6, 0],
        },
    });

    useEffect(() => {
        requestGetUserCount();
        requestGetVisitors();
        requestGetRentVisitors();

        onSearchUserStats();
        onSearchVisitorStats();
        onSearchVisitorRentStats();
    }, []);

    const requestAxiosGetUserCount = async () => {
        const response = await utilAxiosWithAuth().get(Request.USER_COUNT_URL);
        return response.data;
    };

    const {
        loading: loadingGetUserCount,
        error: errorGetUserCount,
        data: resultGetUserCount,
        execute: requestGetUserCount,
    } = useAsyncAxios(requestAxiosGetUserCount);

    useEffect(() => {
        if (resultGetUserCount === null) return;
        console.log("resultGetUserCount", resultGetUserCount);
        setUserCount(resultGetUserCount.userCount);
    }, [resultGetUserCount]);

    useEffect(() => {
        if (errorGetUserCount === null) return;
        console.log("errorGetUserCount", errorGetUserCount);
    }, [errorGetUserCount]);

    const requestAxiosGetUserStats = async (params: SearchUserStatsType) => {
        console.log(params);
        const response = await utilAxiosWithAuth().get(Request.USER_STATS_URL, { params: params });
        return response.data;
    };

    const {
        loading: loadingGetUserStats,
        error: errorGetUserStats,
        data: resultGetUserStats,
        execute: requestGetUserStats,
    } = useAsyncAxios(requestAxiosGetUserStats);

    useEffect(() => {
        if (resultGetUserStats === null) return;
        console.log("resultGetUserStats", resultGetUserStats);
        setUserStats(resultGetUserStats.userStats);
    }, [resultGetUserStats]);

    useEffect(() => {
        if (errorGetUserStats === null) return;
        console.log("errorGetUserStats", errorGetUserStats);
    }, [errorGetUserStats]);

    const requestAxiosGetVisitors = async () => {
        const response = await utilAxiosWithAuth().get(Request.VISITOR_COUNT_URL);
        return response.data;
    };

    const {
        loading: loadingGetVisitors,
        error: errorGetVisitors,
        data: resultGetVisitors,
        execute: requestGetVisitors,
    } = useAsyncAxios(requestAxiosGetVisitors);

    useEffect(() => {
        if (resultGetVisitors === null) return;
        console.log("resultGetVisitors", resultGetVisitors);
        setVisitorCount(resultGetVisitors.visitorCount);
    }, [resultGetVisitors]);

    useEffect(() => {
        if (errorGetVisitors === null) return;
        console.log("errorGetVisitors", errorGetVisitors);
    }, [errorGetVisitors]);

    const requestAxiosGetVisitorStats = async (params: SearchUserStatsType) => {
        console.log(params);
        const response = await utilAxiosWithAuth().get(Request.VISITOR_STATS_URL, { params: params });
        return response.data;
    };

    const {
        loading: loadingGetVisitorStats,
        error: errorGetVisitorStats,
        data: resultGetVisitorStats,
        execute: requestGetVisitorStats,
    } = useAsyncAxios(requestAxiosGetVisitorStats);

    useEffect(() => {
        if (resultGetVisitorStats === null) return;
        console.log("resultGetVisitorStats", resultGetVisitorStats);
        setVisitorStats(resultGetVisitorStats.visitorStats);
    }, [resultGetVisitorStats]);

    useEffect(() => {
        if (errorGetVisitorStats === null) return;
        console.log("errorGetVisitorStats", errorGetVisitorStats);
    }, [errorGetVisitorStats]);

    const requestAxiosGetRentVisitors = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_VISITOR_COUNT_URL);
        return response.data;
    };

    const {
        loading: loadingGetRentVisitors,
        error: errorGetRentVisitors,
        data: resultGetRentVisitors,
        execute: requestGetRentVisitors,
    } = useAsyncAxios(requestAxiosGetRentVisitors);

    useEffect(() => {
        if (resultGetRentVisitors === null) return;
        console.log("resultGetRentVisitors", resultGetRentVisitors);
        setRentVisitorCount(resultGetRentVisitors.rentVisitorCount);
    }, [resultGetRentVisitors]);

    useEffect(() => {
        if (errorGetRentVisitors === null) return;
        console.log("errorGetRentVisitors", errorGetRentVisitors);
    }, [errorGetRentVisitors]);

    const requestAxiosGetVisitorRentStats = async (params: SearchUserStatsType) => {
        console.log(params);
        const response = await utilAxiosWithAuth().get(Request.RENT_VISITOR_STATS_URL, { params: params });
        return response.data;
    };

    const {
        loading: loadingGetVisitorRentStats,
        error: errorGetVisitorRentStats,
        data: resultGetVisitorRentStats,
        execute: requestGetVisitorRentStats,
    } = useAsyncAxios(requestAxiosGetVisitorRentStats);

    useEffect(() => {
        if (resultGetVisitorRentStats === null) return;
        console.log("resultGetVisitorRentStats", resultGetVisitorRentStats);
        setVisitorRentStats(resultGetVisitorRentStats.rentVisitorStats);
    }, [resultGetVisitorRentStats]);

    useEffect(() => {
        if (errorGetVisitorRentStats === null) return;
        console.log("errorGetVisitorRentStats", errorGetVisitorRentStats);
    }, [errorGetVisitorRentStats]);

    const onChangedSearchUserDate = (type: string, value: string) => {
        if (type === "startDate") setSearchUserStartDate(moment(value).format(Common.FORMAT_DATE));
        else if (type === "endDate") setSearchUserEndDate(moment(value).format(Common.FORMAT_DATE));
    };

    const onSearchUserRefresh = () => {
        setSearchUserGroupBy("DAY");
        setSearchUserStartDate(moment().subtract(30, "d").format(Common.FORMAT_DATE));
        setSearchUserEndDate(moment().format(Common.FORMAT_DATE));
    };

    const onSearchUserStats = () => {
        let startDate = searchUserStartDate;
        let endDate = searchUserEndDate;

        if (searchUserGroupBy === "MONTH") {
            startDate = moment(searchUserStartDate).startOf("month").format(Common.FORMAT_DATE);
            endDate = moment(searchUserEndDate).add(1, "M").startOf("month").format(Common.FORMAT_DATE);
        } else if (searchUserGroupBy === "YEAR") {
            startDate = moment(searchUserStartDate).startOf("year").format(Common.FORMAT_DATE);
            endDate = moment(searchUserEndDate).add(12, "M").startOf("year").format(Common.FORMAT_DATE);
        } else if (searchUserGroupBy === "DAY") {
            endDate = moment(searchUserEndDate).add(1, "d").format(Common.FORMAT_DATE);
        }

        requestGetUserStats({
            startDate: startDate,
            endDate: endDate,
            period: searchUserGroupBy,
        });
    };

    const makeChartDataName = (type: string, data: Types.UserStatsItemDto) => {
        let name = "";

        if (type === "YEAR") {
            name = data.year.toString() + "년";
        } else if (type === "MONTH") {
            name = data.year.toString() + "년 " + data.month.toString() + "월";
        } else {
            name = data.year.toString() + String.dash + Utils.to2Digit(data.month) + String.dash + Utils.to2Digit(data.day);
        }

        return name;
    };

    useEffect(() => {
        if (userStats) {
            const userAccumulateCountDatas: ChartElement[] = [];
            const userCountDatas: ChartElement[] = [];

            let baseCount = userStats.baseCount;
            let date = moment(searchUserStartDate);
            const endDate = moment(searchUserEndDate);
            const unitOfTime = (userStats.period.toLocaleLowerCase() + "s") as DurationInputArg2;

            while (date.isSameOrBefore(endDate)) {
                const statData = userStats.userStatsItems.find(
                    (stat) => stat.year === date.year() && (stat.month ?? 1) === date.month() + 1 && (stat.day ?? 1) === date.date()
                ) ?? { year: date.year(), month: date.month() + 1, day: date.date(), count: 0 };
                const time = makeChartDataName(userStats.period, statData);

                userAccumulateCountDatas.push({ x: time, y: statData.count + baseCount });
                userCountDatas.push({ x: time, y: statData.count });

                baseCount += statData.count;
                date.add(1, unitOfTime);
            }

            setChartSeriesUser([
                {
                    name: "누적 회원 수",
                    type: "line",
                    data: userAccumulateCountDatas,
                },
                {
                    name: "회원 증가량",
                    type: "column",
                    data: userCountDatas,
                },
            ]);
        }
    }, [userStats]);

    const onChangedSearchVisitorDate = (type: string, value: string) => {
        if (type === "startDate") setSearchVisitorStartDate(moment(value).format(Common.FORMAT_DATE));
        else if (type === "endDate") setSearchVisitorEndDate(moment(value).format(Common.FORMAT_DATE));
    };

    const onSearchVisitorRefresh = () => {
        setSearchVisitorGroupBy("DAY");
        setSearchVisitorStartDate(moment().subtract(30, "d").format(Common.FORMAT_DATE));
        setSearchVisitorEndDate(moment().format(Common.FORMAT_DATE));
    };

    const onSearchVisitorStats = () => {
        let startDate = searchVisitorStartDate;
        let endDate = searchVisitorEndDate;

        if (searchVisitorGroupBy === "MONTH") {
            startDate = moment(searchVisitorStartDate).startOf("month").format(Common.FORMAT_DATE);
            endDate = moment(searchVisitorEndDate).add(1, "M").startOf("month").format(Common.FORMAT_DATE);
        } else if (searchVisitorGroupBy === "YEAR") {
            startDate = moment(searchVisitorStartDate).startOf("year").format(Common.FORMAT_DATE);
            endDate = moment(searchVisitorEndDate).add(12, "M").startOf("year").format(Common.FORMAT_DATE);
        } else if (searchUserGroupBy === "DAY") {
            endDate = moment(searchVisitorEndDate).add(1, "d").format(Common.FORMAT_DATE);
        }

        requestGetVisitorStats({
            startDate: startDate,
            endDate: endDate,
            period: searchVisitorGroupBy,
        });
    };

    useEffect(() => {
        if (visitorStats) {
            const visitorAccumulateCountDatas: ChartElement[] = [];
            const visitorCountDatas: ChartElement[] = [];

            let baseCount = visitorStats.baseCount;
            let date = moment(searchVisitorStartDate);
            const endDate = moment(searchVisitorEndDate);
            const unitOfTime = (visitorStats.period.toLocaleLowerCase() + "s") as DurationInputArg2;

            while (date.isSameOrBefore(endDate)) {
                const statData = visitorStats.visitorStatsItems.find(
                    (stat) => stat.year === date.year() && (stat.month ?? 1) === date.month() + 1 && (stat.day ?? 1) === date.date()
                ) ?? { year: date.year(), month: date.month() + 1, day: date.date(), count: 0 };
                const time = makeChartDataName(visitorStats.period, statData);

                visitorAccumulateCountDatas.push({ x: time, y: statData.count + baseCount });
                visitorCountDatas.push({ x: time, y: statData.count });

                baseCount += statData.count;
                date.add(1, unitOfTime);
            }

            setChartSeriesVisitor([
                {
                    name: "누적 방문자 수",
                    type: "line",
                    data: visitorAccumulateCountDatas,
                },
                {
                    name: "방문자 증가량",
                    type: "column",
                    data: visitorCountDatas,
                },
            ]);
        }
    }, [visitorStats]);

    const onChangedSearchVisitorRentDate = (type: string, value: string) => {
        if (type === "startDate") setSearchVisitorRentStartDate(moment(value).format(Common.FORMAT_DATE));
        else if (type === "endDate") setSearchVisitorRentEndDate(moment(value).format(Common.FORMAT_DATE));
    };

    const onSearchVisitorRentRefresh = () => {
        setSearchVisitorRentGroupBy("DAY");
        setSearchVisitorRentStartDate(moment().subtract(30, "d").format(Common.FORMAT_DATE));
        setSearchVisitorRentEndDate(moment().format(Common.FORMAT_DATE));
    };

    const onSearchVisitorRentStats = () => {
        let startDate = searchVisitorRentStartDate;
        let endDate = searchVisitorRentEndDate;

        if (searchVisitorRentGroupBy === "MONTH") {
            startDate = moment(searchVisitorRentStartDate).startOf("month").format(Common.FORMAT_DATE);
            endDate = moment(searchVisitorRentEndDate).add(1, "M").startOf("month").format(Common.FORMAT_DATE);
        } else if (searchVisitorRentGroupBy === "YEAR") {
            startDate = moment(searchVisitorRentStartDate).startOf("year").format(Common.FORMAT_DATE);
            endDate = moment(searchVisitorRentEndDate).add(12, "M").startOf("year").format(Common.FORMAT_DATE);
        } else if (searchUserGroupBy === "DAY") {
            endDate = moment(searchVisitorRentEndDate).add(1, "d").format(Common.FORMAT_DATE);
        }

        requestGetVisitorRentStats({
            startDate: startDate,
            endDate: endDate,
            period: searchVisitorRentGroupBy,
        });
    };

    useEffect(() => {
        if (visitorRentStats) {
            const visitorRentAccumulateCountDatas: ChartElement[] = [];
            const visitorRentCountDatas: ChartElement[] = [];

            let baseCount = visitorRentStats.baseCount;
            let date = moment(searchVisitorRentStartDate);
            const endDate = moment(searchVisitorRentEndDate);
            const unitOfTime = (visitorRentStats.period.toLocaleLowerCase() + "s") as DurationInputArg2;

            while (date.isSameOrBefore(endDate)) {
                const statData = visitorRentStats.rentVisitorStatsItems.find(
                    (stat) => stat.year === date.year() && (stat.month ?? 1) === date.month() + 1 && (stat.day ?? 1) === date.date()
                ) ?? { year: date.year(), month: date.month() + 1, day: date.date(), count: 0 };
                const time = makeChartDataName(visitorRentStats.period, statData);

                visitorRentAccumulateCountDatas.push({ x: time, y: statData.count + baseCount });
                visitorRentCountDatas.push({ x: time, y: statData.count });

                baseCount += statData.count;
                date.add(1, unitOfTime);
            }

            setChartSeriesVisitorRent([
                {
                    name: "누적 방문자 수",
                    type: "line",
                    data: visitorRentAccumulateCountDatas,
                },
                {
                    name: "방문자 증가량",
                    type: "column",
                    data: visitorRentCountDatas,
                },
            ]);
        }
    }, [visitorRentStats]);

    return (
        <div className="container">
            <Space size={30} direction="vertical" style={{ width: "100%" }}>
                <h3 className="title">통합 대시보드</h3>
                <div>
                    <Row justify={"space-between"}>
                        <Col span={6}>
                            <Divider orientation="left">
                                <span>통합 회원가입자 수</span>
                            </Divider>
                        </Col>
                    </Row>
                    <Row gutter={20}>
                        <Col xs={24} md={12} className="gutter-row">
                            <StatsCard
                                title={Strings.total_user_count}
                                value={userCount?.totalCount !== undefined ? userCount?.totalCount.toString() : Strings.dash}
                                unit={Strings.count_user}
                            />
                        </Col>
                        <Col xs={24} md={12} className="gutter-row">
                            <StatsCard
                                title={Strings.today_user_count}
                                value={userCount?.todayCount !== undefined ? userCount?.todayCount.toString() : Strings.dash}
                                unit={Strings.count_user}
                            />
                        </Col>
                    </Row>
                    <Space direction="vertical" size={20} style={{ width: "100%" }}>
                        <Space>
                            <Select
                                style={{ width: 150 }}
                                placeholder="전체"
                                value={searchUserGroupBy}
                                onChange={(value) => {
                                    setSearchUserGroupBy(value);
                                }}
                            >
                                <Select.Option value={"DAY"}>일별</Select.Option>
                                <Select.Option value={"MONTH"}>월별</Select.Option>
                                <Select.Option value={"YEAR"}>연별</Select.Option>
                            </Select>
                            <SearchDate
                                startDate={searchUserStartDate}
                                endDate={searchUserEndDate}
                                onSearch={onChangedSearchUserDate}
                                picker={searchUserGroupBy === "MONTH" ? "month" : searchUserGroupBy === "YEAR" ? "year" : "date"}
                                format={searchUserGroupBy === "MONTH" ? "YYYY-MM" : searchUserGroupBy === "YEAR" ? "YYYY" : "YYYY-MM-DD"}
                            />
                            <Button onClick={onSearchUserRefresh}>검색 초기화</Button>
                            <Button onClick={onSearchUserStats}>검색</Button>
                        </Space>
                        <DataChart height="300px" series={chartSeriesUser} option={commonChartOptions} />
                    </Space>
                </div>
                <div>
                    <Row justify={"space-between"}>
                        <Col span={6}>
                            <Divider orientation="left">
                                <span>판매 사이트</span>
                            </Divider>
                        </Col>
                    </Row>
                    <Row gutter={20}>
                        <Col xs={24} md={12} className="gutter-row">
                            <StatsCard
                                title={Strings.total_visitor_count}
                                value={visitorCount?.totalCount !== undefined ? visitorCount?.totalCount.toString() : Strings.dash}
                                unit={Strings.count_user}
                            />
                        </Col>
                        <Col xs={24} md={12} className="gutter-row">
                            <StatsCard
                                title={Strings.today_visitor_count}
                                value={visitorCount?.todayCount !== undefined ? visitorCount?.todayCount.toString() : Strings.dash}
                                unit={Strings.count_user}
                            />
                        </Col>
                    </Row>
                    <Space direction="vertical" size={20} style={{ width: "100%" }}>
                        <Space>
                            <Select
                                style={{ width: 150 }}
                                placeholder="전체"
                                value={searchVisitorGroupBy}
                                onChange={(value) => {
                                    setSearchVisitorGroupBy(value);
                                }}
                            >
                                <Select.Option value={"DAY"}>일별</Select.Option>
                                <Select.Option value={"MONTH"}>월별</Select.Option>
                                <Select.Option value={"YEAR"}>연별</Select.Option>
                            </Select>
                            <SearchDate
                                startDate={searchVisitorStartDate}
                                endDate={searchVisitorEndDate}
                                onSearch={onChangedSearchVisitorDate}
                                picker={searchVisitorGroupBy === "MONTH" ? "month" : searchVisitorGroupBy === "YEAR" ? "year" : "date"}
                                format={searchVisitorGroupBy === "MONTH" ? "YYYY-MM" : searchVisitorGroupBy === "YEAR" ? "YYYY" : "YYYY-MM-DD"}
                            />
                            <Button onClick={onSearchVisitorRefresh}>검색 초기화</Button>
                            <Button onClick={onSearchVisitorStats}>검색</Button>
                        </Space>
                        <DataChart height="300px" series={chartSeriesVisitor} option={commonChartOptions} />
                    </Space>
                </div>
                <div>
                    <Row justify={"space-between"}>
                        <Col span={6}>
                            <Divider orientation="left">
                                <span>렌트 사이트</span>
                            </Divider>
                        </Col>
                    </Row>
                    <Row gutter={20}>
                        <Col xs={24} md={12} className="gutter-row">
                            <StatsCard
                                title={Strings.total_visitor_count}
                                value={rentVisitorCount?.totalCount !== undefined ? rentVisitorCount?.totalCount.toString() : Strings.dash}
                                unit={Strings.count_user}
                            />
                        </Col>
                        <Col xs={24} md={12} className="gutter-row">
                            <StatsCard
                                title={Strings.today_visitor_count}
                                value={rentVisitorCount?.todayCount !== undefined ? rentVisitorCount?.todayCount.toString() : Strings.dash}
                                unit={Strings.count_user}
                            />
                        </Col>
                    </Row>
                    <Space direction="vertical" size={20} style={{ width: "100%" }}>
                        <Space>
                            <Select
                                style={{ width: 150 }}
                                placeholder="전체"
                                value={searchVisitorRentGroupBy}
                                onChange={(value) => {
                                    setSearchVisitorRentGroupBy(value);
                                }}
                            >
                                <Select.Option value={"DAY"}>일별</Select.Option>
                                <Select.Option value={"MONTH"}>월별</Select.Option>
                                <Select.Option value={"YEAR"}>연별</Select.Option>
                            </Select>
                            <SearchDate
                                startDate={searchVisitorRentStartDate}
                                endDate={searchVisitorRentEndDate}
                                onSearch={onChangedSearchVisitorRentDate}
                                picker={searchVisitorRentGroupBy === "MONTH" ? "month" : searchVisitorRentGroupBy === "YEAR" ? "year" : "date"}
                                format={searchVisitorRentGroupBy === "MONTH" ? "YYYY-MM" : searchVisitorRentGroupBy === "YEAR" ? "YYYY" : "YYYY-MM-DD"}
                            />
                            <Button onClick={onSearchVisitorRentRefresh}>검색 초기화</Button>
                            <Button onClick={onSearchVisitorRentStats}>검색</Button>
                        </Space>
                        <DataChart height="300px" series={chartSeriesVisitorRent} option={commonChartOptions} />
                    </Space>
                </div>
            </Space>
        </div>
    );
}

export default Dashboard;
